import { useAppDispatch } from '@/hooks'
import { errToMessage } from '@/utils/errors/errorUtils'
import { RootState } from '@/reducers'

export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: ApiResultError }

type ApiResultError = {
  message: string
  // This is used in the event an exception is actually thrown
  // and not just an API error
  error?: Error
  status?: number
}

export default function useApiDispatch() {
  const dispatch = useAppDispatch()
  const DEFAULT_ERROR = 'An error has occurred. Please try again'

  const apiDispatch = async function <T>(
    action: (dispatch: unknown, getState: () => RootState) => Promise<unknown>,
  ): Promise<ApiResult<T>> {
    try {
      const res = await dispatch(action)

      if (res.type.toString().includes('FAILURE')) {
        const error =
          res.payload?.message && typeof res.payload.message === 'string'
            ? res.payload.message
            : DEFAULT_ERROR
        const status = res.payload?.status

        return { ok: false, error: { message: error, status } }
      }

      return { ok: true, data: res.payload as T }
    } catch (error) {
      const message = errToMessage(error)
      if (error instanceof Error) {
        return { ok: false, error: { message, error } }
      }

      return { ok: false, error: { message } }
    }
  }

  return {
    apiDispatch,
  }
}
